import { ArrayKeys, PersonForm } from 'src/app/@shared/form/form.types';
import { BaseListFilterPage, CONTAIN_FILTER_SEPARATOR } from 'src/app/@shared/base-list-filter-page';
/* eslint-disable max-lines */
import { ClientFilterObject } from '@common/filter.decorator';
import { Component, Input, ViewChild, inject } from '@angular/core';
import { IonContent } from '@ionic/angular';
import { LOCATION_SEARCH_KEY, MULTI_FIELD_SEARCH_KEY } from '@common/search-filter.constants';
import { NavigationEnd } from '@angular/router';
import { PERSON_FILTER_FORM } from 'src/app/@shared/form/form-config';
import { PersonModel, PersonSource } from 'src/app/person/@shared/person.model';
import { PersonService } from 'src/app/person/@shared/person.service';
import { filter, lastValueFrom } from 'rxjs';
import { getKeysByFormConfig } from 'src/app/@shared/form/form-helper';
import { getPersonFilterResultByWhereFilter } from 'src/app/@shared/person-filter.adapter';
import { takeUntil } from 'rxjs/operators';

import { ContactPersonService } from 'src/app/cluster/@shared/contact-person.service';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';

const SKELETON_ITEM_COUNT = 30;

const FORM_FILTER_KEYS = getKeysByFormConfig(PERSON_FILTER_FORM);
// !!! IMPORTANT: we have to list all search/filter keys here which have array values !!!
const ARRAY_VALUES: ArrayKeys<PersonForm>[] = ['qualifications', 'media'];

@Component({
  selector: 'page-person-list',
  standalone: false,
  styleUrls: ['./person-list.page.scss'],
  templateUrl: './person-list.page.html',
})
export class PersonListPage extends BaseListFilterPage<PersonModel, PersonSource> {
  @ViewChild('ListContent', {
    static: false,
  }) public content: IonContent;

  @Input() public contactPersons = false;
  @Input() public isMultiSelect: boolean;
  @Input() public selectedItems: PersonModel[] = [];
  @Input() public enableAllSelection: boolean = false;
  @Input() public selectedAllState: { excludedIds: string[]; isAllSelected: boolean; whereFilter?: any } | null = null;

  public readonly pageTrackName = 'PersonListPage';

  public path = 'persons';
  public items: { [key: string]: PersonModel[] } = {};
  public currentListType: 'thumbnail' | 'card' = 'thumbnail';
  public totalItemsInDatabase: number;

  public rootPath: string;
  public activatedRouteId: string;
  public selectedItemIds: string[] = [];
  public allSelectedInFilter: boolean = false;
  public excludedItemIds = new Set<string>();
  public currentSecondarySearchValue: string = '';
  public isSearchInfoPopoverOpen: boolean = false;
  public searchInfoPopoverEvent?: Event;
  public isPublicWebApp = isPublicWebAppBuild();

  private personService = inject(PersonService);
  private contactPersonService = inject(ContactPersonService);

  private get listService(): PersonService | ContactPersonService {
    return this.contactPersons
      ? this.contactPersonService
      : this.personService;
  }

  public onItemClicked(item: PersonModel): Promise<boolean> {
    if (this.isModal) {
      this.toggleSelectedItem(item);

      return Promise.resolve(true);
    }

    return this.navigate([item.id],
      {
        queryParams: this.queryParams,
        relativeTo: this.activatedRoute,
        state: {
          item,
        },
      },
      'root');
  }

  public dismiss(data?: any): void {
    this.modalController.dismiss(data).catch();
  }

  public submit(): void {
    const sorted = this.selectedItems
      .slice()
      .sort((a, b) => {
        if (a.displayName < b.displayName) {
          return -1;
        } else if (a.displayName === b.displayName) {
          return 0;
        }

        return 1;
      });

    if (!this.enableAllSelection) {
      this.dismiss(sorted);

      return;
    }

    const selection = {
      excludedIds: Array.from(this.excludedItemIds),
      isAllSelected: this.allSelectedInFilter,
      whereFilter: this.filters[this.currentContentId]?.where || {},
    };

    this.dismiss({
      items: sorted,
      selection,
    } as any);
  }

  public toggleSelectedItem(item: PersonModel): void {
    if (this.allSelectedInFilter) {
      if (this.excludedItemIds.has(item.id)) {
        this.excludedItemIds.delete(item.id);
      } else {
        this.excludedItemIds.add(item.id);
      }

      this.selectedItemIds = this.items[this.currentContentId]
        .filter((currentItem) => {
          return !this.excludedItemIds.has(currentItem.id);
        })
        .map((currentItem) => {
          return currentItem.id;
        });

      this.selectedItems = this.items[this.currentContentId]
        .filter((currentItem) => {
          return this.selectedItemIds.includes(currentItem.id);
        });

      return;
    }

    if (!this.isMultiSelect) {
      this.selectedItems = [item];
      this.selectedItemIds = [item.id];

      return;
    }

    if (this.selectedItems.find((o) => { return o.id === item.id; })) {
      this.selectedItems = this.selectedItems.filter((o) => { return o.id !== item.id; });
    } else {
      this.selectedItems.push(item);
    }

    this.selectedItemIds = this.selectedItems.map((o) => { return o.id; });
  }

  public toggleSelectAllInFilter(): void {
    this.allSelectedInFilter = !this.allSelectedInFilter;
    this.excludedItemIds = new Set();

    if (this.allSelectedInFilter) {
      this.selectedItemIds = this.items[this.currentContentId]
        .map((item) => {
          return item?.id;
        })
        .filter(Boolean);
      this.selectedItems = this.items[this.currentContentId]
        .filter(Boolean);
    } else {
      this.selectedItemIds = [];
      this.selectedItems = [];
    }
  }

  public onFilterButtonClicked(): void {
    this.openFilter(
      FORM_FILTER_KEYS, 'person', null, null, this.contactPersons
        ? 'filterContactPerson'
        : 'filterPerson',
    ).catch();
  }

  public openSearchInfoPopover(event: Event): void {
    this.searchInfoPopoverEvent = event;
    this.isSearchInfoPopoverOpen = true;
  }

  public onSecondarySearchValueUpdated(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    if (value === this.currentSecondarySearchValue) {
      return;
    }

    this.currentSecondarySearchValue = value;
    this.updateQueryParamsBySearchValue(value, LOCATION_SEARCH_KEY);

    if (this.content) {
      this.content.scrollToTop().catch();
    } else {
      console.error('onSecondarySearchValueUpdated: this.content is not defined for', this.currentContentId);
    }

    if (this.isModal) {
      this.onQueryParamsUpdated(this.currentContentId);
    }
  }

  // override base-list.page ngOnInit
  public ngOnInit(): void {
    if (this.contactPersons) { this.path = 'contact-persons'; }
    if (this.isModal) {
      this.selectedItemIds = this.selectedItems.map((o) => {
        return o.id;
      });

      if (this.enableAllSelection && this.selectedAllState?.isAllSelected) {
        this.allSelectedInFilter = true;
        this.excludedItemIds = new Set(this.selectedAllState.excludedIds || []);
      }
    } else {
      this.rootPath = this.getRootPath(this.router.url, this.path);
      this.activatedRouteId = this.getDetailIdFromUrl(this.router.url, this.path);
    }

    this.listIds = this.isModal
      ? [`${this.path}-modal`]
      : [this.path];
    [this.currentContentId] = this.listIds;

    if (this.isModal
      && this.enableAllSelection
      && this.selectedAllState?.whereFilter
      && Object.keys(this.queryParams || {}).length === 0) {
      this.seedQueryParamsFromPersistedSelectionFilter();
    }

    this.initList(this.currentContentId);
  }

  protected subscribeToContent(): void {
    if (!this.isModal) {
      // observe detail route changes
      this.router.events
        .pipe(takeUntil(this.ngUnsubscribe),
          filter((event) => { return event instanceof NavigationEnd; }))
        .subscribe((ev: NavigationEnd) => {
          if (ev instanceof NavigationEnd && ev.url) {
            this.activatedRouteId = this.getDetailIdFromUrl(this.router.url, this.path);
          }
        });

      // observe search and filter changes
      //
      // queryParams (sanitized) should always reflect applied filter
      // currently we only do this for our where filter
      // params like order or offset are not part of query params yet
      this.activatedRoute.queryParams.subscribe((queryParams) => {
        this.queryParams = {
          ...queryParams,
        };

        this.onQueryParamsUpdated(this.currentContentId);
      });
    }

    this.subscribeToCurrentList(this.currentContentId);
  }

  protected hasMoreItems(id: string): boolean {
    return this.listService.hasMoreItems(id);
  }

  protected async reloadContent(id: string): Promise<void> {
    try {
      this.setFilter(id, {
        skip: 0,
      });
      await lastValueFrom(this.listService.loadContent(id, {
        filter: this.filters[id],
        type: 'list',
      }));
    } catch (error) {
      this.onRequestFailed(id, error);
    }
  }

  protected async loadNextItems(id: string): Promise<void> {
    try {
      this.setFilter(id, {
        skip: this.items[id].length,
      });
      await lastValueFrom(this.listService.loadContent(id, {
        filter: this.filters[id],
        type: 'list',
      }));
    } catch (error) {
      this.onRequestFailed(id, error);
    }
  }

  protected initList(id: string): void {
    this.viewStates[id] = this.getInitialViewStates(false);
    this.items[id] = Array(SKELETON_ITEM_COUNT).fill(null);
  }

  protected onQueryParamsUpdated(id: string): void {
    const where = this.getWhereFilterByQueryParams(FORM_FILTER_KEYS);
    const multiFieldSearchValue = this.getNormalizedSearchValue(this.queryParams[MULTI_FIELD_SEARCH_KEY]);

    this.currentSecondarySearchValue = this.getNormalizedSearchValue(this.queryParams[LOCATION_SEARCH_KEY]);

    if (multiFieldSearchValue) {
      where[MULTI_FIELD_SEARCH_KEY] = multiFieldSearchValue;
    }

    if (this.currentSecondarySearchValue) {
      where[LOCATION_SEARCH_KEY] = this.currentSecondarySearchValue;
    }

    for (const key of Object.keys(this.queryParams)) {
      if (ARRAY_VALUES.includes(key as ArrayKeys<PersonForm>)) {
        const rawValue = this.queryParams[key];
        const contains = Array.isArray(rawValue)
          ? rawValue
          : rawValue.split(CONTAIN_FILTER_SEPARATOR).filter(Boolean);

        where[key] = {
          contains,
        };
      }
    }

    const currentFilter: ClientFilterObject<PersonSource> = {
      order: {},
      where,
    };

    this.applyFilterSettings(id, currentFilter, this.listService.getMetaData(id), FORM_FILTER_KEYS);

    if (this.allSelectedInFilter) {
      this.selectedAllState ||= {
        excludedIds: [],
        isAllSelected: true,
      };
      this.selectedAllState.whereFilter = currentFilter.where;
    }
  }

  private seedQueryParamsFromPersistedSelectionFilter(): void {
    const persistedSearchValue = this.getNormalizedSearchValue(this.selectedAllState?.whereFilter?.[MULTI_FIELD_SEARCH_KEY]);
    const persistedLocationSearchValue = this.getNormalizedSearchValue(this.selectedAllState?.whereFilter?.[LOCATION_SEARCH_KEY]);

    if (persistedSearchValue) {
      this.queryParams[MULTI_FIELD_SEARCH_KEY] = persistedSearchValue;
    } else {
      delete this.queryParams[MULTI_FIELD_SEARCH_KEY];
    }

    if (persistedLocationSearchValue) {
      this.queryParams[LOCATION_SEARCH_KEY] = persistedLocationSearchValue;
    } else {
      delete this.queryParams[LOCATION_SEARCH_KEY];
    }

    const filterResult = getPersonFilterResultByWhereFilter(this.selectedAllState?.whereFilter as any) as Partial<PersonSource>;

    for (const key of FORM_FILTER_KEYS) {
      const value = this.getQueryParamValue(filterResult[key as keyof PersonSource] as any);

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        this.queryParams[key as string] = value;
      } else {
        delete this.queryParams[key as string];
      }
    }
  }

  private subscribeToCurrentList(id: string): void {
    // when this.isModal = true
    // + just in case queryParams subscription wasn't initially triggered for any reason
    if (!this.filters[id]) {
      this.onQueryParamsUpdated(id);
    }

    this.listService.getContent(id, {
      filter: this.filters[id],
      // reload content if list is opened as modal -> fix modal opening to slow or showing last searched items
      forceReload: this.isModal,
      type: 'list',
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((items): void => {
        if (items) {
          this.items[id] = items;
          this.totalItemsInDatabase = this.listService.getMetaData(id).totalCount;

          if (this.allSelectedInFilter) {
            this.selectedItemIds = items
              .filter((item) => {
                return !this.excludedItemIds.has(item.id);
              })
              .map((item) => {
                return item.id;
              });
            this.selectedItems = items.filter((item) => {
              return this.selectedItemIds.includes(item.id);
            });
          }

          this.onRequestSucceeded(id);
        } else {
          const metadata = this.listService.getMetaData(id);

          this.onRequestFailed(id, metadata.error);
        }
      });
  }

  private getRootPath(route: string, subPath: string): string {
    const [normalizedRoute] = route.split('?');
    const subPathIndex = route.indexOf(subPath);

    if (subPathIndex === -1) {
      console.warn('subPath missing in ', route, subPath);

      return normalizedRoute;
    }

    return normalizedRoute.substring(0, subPathIndex + subPath.length);
  }

  private getDetailIdFromUrl(url: string, basePath: string): string {
    const [normalizedUrl] = url.split('?');
    const parts = normalizedUrl.split('/');
    const index = parts.indexOf(basePath);

    if (index === -1) {
      return '';
    }

    return parts[index + 1];
  }
}
